import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { TaskItem, TodoIntegrationType } from '@demind-inc/core';
import { useRecoilState } from 'recoil';

import { TaskError401Res, tasksApi } from '../api';
import { AddTodoTaskItemProps, TasksByDate } from '../types';
import { taskErrorSnackBarAtom } from '../recoil';
import { getDueDateYYYYMMDD } from '../../helpers';
import { TEAM_TASKS_QUERY_KEY } from '../queries/teamTasks.shared';
import { translateI18n } from '../../i18n';

export interface UseAddTodoTaskParams {
  boardId: string;
  userId: string;
  newTaskInfo: AddTodoTaskItemProps;
  taskId?: string;
  originalTaskId?: string;
}

export const useAddTodoTask = () => {
  const queryClient = useQueryClient();
  const [_, setTaskErrorSnackbar] = useRecoilState(taskErrorSnackBarAtom);

  const addTaskMutation = useMutation({
    mutationFn: ({
      boardId,
      userId,
      newTaskInfo,
      taskId,
      originalTaskId,
    }: UseAddTodoTaskParams) => {
      return tasksApi
        .addTodoTask(boardId, userId, newTaskInfo, taskId, originalTaskId)
        .then(({ data }) => data.taskId);
    },
    onMutate: async ({ boardId, userId, newTaskInfo, taskId }) => {
      const dueDate = getDueDateYYYYMMDD(newTaskInfo.dueDateTime);

      if (!dueDate) {
        const overdueQueryKey = ['lifestack.todo.overdueTasks'];
        await queryClient.cancelQueries({
          queryKey: overdueQueryKey,
        });

        queryClient.setQueriesData<TaskItem[]>({ queryKey: overdueQueryKey }, (prevTasks) => {
          if (!prevTasks) {
            return prevTasks;
          }

          if (newTaskInfo.parentOriginalTaskId) {
            return prevTasks.map((task) => {
              if (task.originalTaskId !== newTaskInfo.parentOriginalTaskId) {
                return task;
              }

              return {
                ...task,
                subTasks: [
                  ...(task.subTasks ?? []),
                  { ...newTaskInfo, boardId, taskId },
                ] as TaskItem[],
              };
            });
          }

          return [...prevTasks, { ...newTaskInfo, boardId, taskId }] as TaskItem[];
        });

        return;
      }

      const queryKey = ['lifestack.todo.tasks', { dueDate }];
      await queryClient.cancelQueries({
        queryKey,
      });
      queryClient.setQueriesData<TasksByDate>({ queryKey }, (prevTasks) => {
        if (newTaskInfo.parentOriginalTaskId) {
          // subtask
          const updatedTasks = prevTasks?.tasks?.map((task) => {
            if (task.originalTaskId !== newTaskInfo.parentOriginalTaskId) {
              return task;
            }
            return {
              ...task,
              subTasks: [
                ...(task.subTasks ?? []),
                { ...newTaskInfo, boardId, taskId },
              ] as TaskItem[],
            };
          });
          return {
            date: prevTasks?.date,
            tasks: updatedTasks,
            updatedAt: new Date().toISOString(),
          };
        }
        return {
          date: prevTasks?.date,
          tasks: [...(prevTasks?.tasks ?? []), { ...newTaskInfo, boardId, taskId }] as TaskItem[],
          updatedAt: new Date().toISOString(),
        };
      });
    },
    onSuccess: (_, { newTaskInfo }) => {
      const dueDate = getDueDateYYYYMMDD(newTaskInfo.dueDateTime);

      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY, type: 'active' });
      queryClient.invalidateQueries({
        queryKey: dueDate ? ['lifestack.todo.tasks', { dueDate }] : ['lifestack.todo.tasks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['lifestack.todo.overdueTasks'],
      });
    },
  });

  useEffect(() => {
    const error = (addTaskMutation.error as AxiosError)?.response;
    if (!error) {
      return;
    }
    if (error.status === 401) {
      setTaskErrorSnackbar({
        status: error.status,
        message: translateI18n('task.error.invalidToken'),
        provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
        action: 'add',
      });
      return;
    }
    setTaskErrorSnackbar({
      status: error.status,
      message: translateI18n('task.error.addFailed'),
      provider: (error.data as TaskError401Res).provider as TodoIntegrationType,
      action: 'add',
    });
  }, [addTaskMutation.error]);

  return {
    addTodoTask: addTaskMutation.mutateAsync,
    isAdding: addTaskMutation.isPending,
    ...addTaskMutation,
  };
};
